/**
 * Email Confirmation Route
 * Handles the link in the confirmation email. No guest guard: most people click
 * it while already signed in, and it must work for them too. The link confirms
 * the address and never signs anyone in.
 */

import React, { useEffect, useRef, useState } from 'react'
import { createFileRoute, useSearch } from '@tanstack/react-router'
import { AuthAlert, AuthButton, AuthNote, AuthShell, AuthSpinner } from '../components/auth/AuthShell'
import { authService } from '../services/auth.service'
import { getAccessToken } from '../utils/auth-token'

interface SearchParams {
  token?: string
}

const VerifyEmailRouteComponent = () => {
  const { token } = useSearch({ from: '/verify-email' }) as SearchParams
  const [status, setStatus] = useState<'working' | 'done' | 'failed'>(token ? 'working' : 'failed')
  const started = useRef(false)

  useEffect(() => {
    if (!token || started.current) return
    started.current = true
    authService
      .verifyEmail(token)
      .then(() => setStatus('done'))
      .catch(() => setStatus('failed'))
  }, [token])

  const signedIn = Boolean(getAccessToken())
  const goOn = () => {
    window.location.href = signedIn ? '/home' : '/login?verified=true'
  }

  if (status === 'working') {
    return (
      <AuthShell title="Confirming your email…">
        <AuthSpinner />
      </AuthShell>
    )
  }

  if (status === 'done') {
    return (
      <AuthShell title="Your email is confirmed" subtitle="Thank you. You can carry on using Charti.">
        <AuthButton onClick={goOn}>{signedIn ? 'Go to Charti' : 'Log in'}</AuthButton>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="This link has expired">
      <AuthAlert tone="info">
        {signedIn
          ? 'Carry on as usual. After your first recording, a bar at the top of Charti lets you ask for a new link.'
          : 'Please log in and carry on. After your first recording, a bar at the top of Charti lets you ask for a new link.'}
      </AuthAlert>
      <AuthButton onClick={() => { window.location.href = signedIn ? '/home' : '/login' }}>
        {signedIn ? 'Go to Charti' : 'Log in'}
      </AuthButton>
      <AuthNote>Links work for 48 hours.</AuthNote>
    </AuthShell>
  )
}

export const Route = createFileRoute('/verify-email')({
  component: VerifyEmailRouteComponent,
})
